import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { MaskLine } from "../components/Reveal";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { submitContact } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

const NAME_PATTERN = /^[A-Za-z]+(?:\s+[A-Za-z]+)*$/;
const PHONE_PATTERN = /^\d{10}$/;

const LineInput = ({ label, error, ...props }) => (
  <div>
    <label className="label mb-3 block text-muted-foreground">{label}</label>
    <input
      {...props}
      className="w-full border-0 border-b border-black/20 bg-transparent px-0 py-3 font-display text-lg text-foreground outline-none transition-colors placeholder:text-black/25 focus:border-primary"
    />
    {error ? <p className="mt-2 text-sm text-primary">{error}</p> : null}
  </div>
);

export default function Contact() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [errors, setErrors] = useState({ name: "", phone: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onNameChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^[A-Za-z\s]*$/.test(value)) {
      setForm((f) => ({ ...f, name: value }));
      setErrors((err) => ({ ...err, name: "" }));
    }
  };

  const onPhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, phone: value }));
    setErrors((err) => ({ ...err, phone: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.subject.trim() ||
      !form.message.trim()
    ) {
      toast.error(t("contact.errRequired"));
      return;
    }
    if (!NAME_PATTERN.test(form.name.trim())) {
      setErrors((err) => ({ ...err, name: t("contact.errName") }));
      return;
    }
    if (!PHONE_PATTERN.test(form.phone)) {
      setErrors((err) => ({ ...err, phone: t("contact.errPhone") }));
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
      setErrors({ name: "", phone: "" });
    } catch (err) {
      toast.error(err.message || t("contact.errGeneric"));
    } finally {
      setSending(false);
    }
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": SEO_CONFIG.contact.title,
    "description": SEO_CONFIG.contact.description,
    "url": SEO_CONFIG.contact.canonical,
    "mainEntity": {
      "@type": "Organization",
      "name": "Ashlar Buildchem",
      "telephone": "+91-1800-309-0808",
      "email": "care@ashlarbuildchem.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "247(NE), Scheme 4C, Jamunapuri",
        "addressLocality": "Jaipur",
        "addressRegion": "Rajasthan",
        "postalCode": "302039",
        "addressCountry": "IN"
      }
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO {...SEO_CONFIG.contact} schema={contactSchema} />
      <div className="mx-auto grid max-w-[1600px] gap-px bg-black/10 px-0 lg:grid-cols-2">
        {/* Left: form */}
        <div className="bg-background px-6 py-20 md:px-16 md:py-28">
          <span className="label text-primary">{t("contact.eyebrow")}</span>
          <h1
            className={`mt-6 font-display text-4xl font-bold md:text-7xl ${
              lang === "hi"
                ? "normal-case leading-[1.15] tracking-normal"
                : "uppercase leading-[0.85] tracking-tighter"
            }`}
          >
            <MaskLine delay={0.05}>{t("contact.h1a")}</MaskLine>
            <MaskLine delay={0.16}>{t("contact.h1b")}</MaskLine>
          </h1>
          <p className="mt-6 max-w-md text-base text-muted-foreground">
            {t("contact.intro")}
          </p>

          <form onSubmit={submit} className="mt-12 space-y-8" data-testid="contact-form">
            <div className="grid gap-8 sm:grid-cols-2">
              <LineInput
                label={t("contact.fullName")}
                placeholder={t("contact.namePlaceholder")}
                value={form.name}
                onChange={onNameChange}
                error={errors.name}
                data-testid="contact-name"
              />
              <LineInput label={t("contact.email")} type="email" placeholder={t("contact.emailPlaceholder")} value={form.email} onChange={set("email")} data-testid="contact-email" />
              <LineInput
                label={t("contact.phone")}
                placeholder="+91"
                value={form.phone}
                onChange={onPhoneChange}
                inputMode="numeric"
                maxLength={10}
                error={errors.phone}
                data-testid="contact-phone"
              />
              <LineInput label={t("contact.company")} placeholder={t("contact.optional")} value={form.company} onChange={set("company")} data-testid="contact-company" />
            </div>
            <LineInput label={t("contact.subject")} placeholder={t("contact.subjectPlaceholder")} value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
            <div>
              <label className="label mb-3 block text-muted-foreground">{t("contact.message")}</label>
              <textarea
                rows={4}
                placeholder={t("contact.messagePlaceholder")}
                value={form.message}
                onChange={set("message")}
                data-testid="contact-message"
                className="w-full resize-none border-0 border-b border-black/20 bg-transparent px-0 py-3 font-display text-lg text-foreground outline-none transition-colors placeholder:text-black/25 focus:border-primary"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-primary px-10 py-5 text-primary-foreground transition-colors duration-200 hover:bg-foreground hover:text-background disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <span className="label">{sending ? t("contact.sending") : t("contact.send")}</span>
              {!sending && <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />}
            </button>
          </form>
        </div>

        {/* Right: info */}
        <div className="relative flex flex-col justify-between overflow-hidden bg-secondary/30 px-6 py-20 md:px-16 md:py-28">
          <img
            src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1600&auto=format&fit=crop"
            alt="Architecture"
            className="absolute inset-0 h-full w-full object-cover opacity-15 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 to-background/90" />
          <div className="relative space-y-10">
            <a href="tel:18003090808" data-testid="contact-phone-link" className="group block border-b border-black/10 pb-8">
              <span className="label text-muted-foreground">{t("contact.tollFree")}</span>
              <div className="mt-3 flex min-w-0 items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-primary" />
                <span className="min-w-0 break-all font-display text-3xl font-semibold transition-colors group-hover:text-primary md:text-4xl">1800 309 0808</span>
              </div>
            </a>
            <a href="mailto:care@ashlarbuildchem.com" data-testid="contact-email-link" className="group block border-b border-black/10 pb-8">
              <span className="label text-muted-foreground">{t("contact.emailLabel")}</span>
              <div className="mt-3 flex min-w-0 items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-primary" />
                <span className="min-w-0 break-all font-display text-xl font-semibold transition-colors group-hover:text-primary md:text-3xl">care@ashlarbuildchem.com</span>
              </div>
            </a>
            <div>
              <span className="label text-muted-foreground">{t("contact.office")}</span>
              <div className="mt-3 flex items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <span className="font-display text-xl leading-snug md:text-2xl">
                  247(NE), Scheme 4C, Jamunapuri,<br />Jaipur — 302039, Rajasthan, India
                </span>
              </div>
            </div>
          </div>
          <div className="relative mt-16">
            <span className="label text-primary">{t("contact.badges")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
